import React from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import useSuperAdminUsersStore from '@/stores/SuperAdminStore/SuperAdminUsersStore';
import { LoadingSpinner } from '@/Components/common';
import './DeactivationLogTab.css';

const DeactivationLogTab: React.FC = () => {
  const {
    deactivationLogs,
    logsPagination,
    loadingLogs,
    reactivating,
    reactivateUser,
    fetchDeactivationLogs,
  } = useSuperAdminUsersStore();

  const handleReactivate = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to reactivate ${userName}?`)) {
      return;
    }

    const result = await reactivateUser(userId);

    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleExport = () => {
    if (deactivationLogs.length === 0) {
      toast.info('No logs to export');
      return;
    }

    const headers = 'User,Department,Deactivated By,Date,Reason';
    const rows = deactivationLogs.map(
      (log) =>
        `"${log.userName}","${log.department}","${log.deactivatedBy}","${log.deactivatedDate}","${log.reason}"`
    );
    const csv = [headers, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deactivation_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Log exported successfully');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="admin-user-deactivation">
      <div className="admin-user-deactivation-header">
        <div className="admin-user-deactivation-info">
          <h2>Deactivation Log</h2>
          <p>
            Audit trail of deactivated user accounts
            {logsPagination && ` (${logsPagination.total} total)`}
          </p>
        </div>
        <div className="admin-user-deactivation-actions">
          <button
            className="admin-user-deactivation-refresh-btn"
            onClick={() => fetchDeactivationLogs()}
            disabled={loadingLogs}
          >
            <RefreshCw size={18} className={loadingLogs ? 'spinning' : ''} />
            Refresh
          </button>
          <button className="admin-user-deactivation-export-btn" onClick={handleExport}>
            <Download size={18} />
            Export Log
          </button>
        </div>
      </div>

      <div className="admin-user-deactivation-table-container">
        {loadingLogs ? (
          <div className="admin-user-deactivation-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : deactivationLogs.length === 0 ? (
          <div className="admin-user-deactivation-empty">
            <p>No deactivation records found</p>
          </div>
        ) : (
          <table className="admin-user-deactivation-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>DEPARTMENT</th>
                <th>DEACTIVATED BY</th>
                <th>DEACTIVATION DATE</th>
                <th>REASON</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {deactivationLogs.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className="admin-user-deactivation-user-cell">
                      <div className="admin-user-deactivation-avatar">
                        {log.userName
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()}
                      </div>
                      <div>
                        <div className="admin-user-deactivation-name">{log.userName}</div>
                      </div>
                    </div>
                  </td>
                  <td>{log.department}</td>
                  <td>{log.deactivatedBy}</td>
                  <td>{formatDate(log.deactivatedDate)}</td>
                  <td>
                    <span className="admin-user-deactivation-reason">{log.reason || 'No reason provided'}</span>
                  </td>
                  <td>
                    <button
                      className="admin-user-deactivation-reactivate-btn"
                      onClick={() => handleReactivate(log.userId, log.userName)}
                      disabled={reactivating}
                    >
                      {reactivating ? 'Processing...' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DeactivationLogTab;
